// Mock Sponsors Data
// Extracted from SponsorshipIntegration.tsx

export interface SponsorLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  hours: Record<string, any>;
  specialServices: string[];
}

export interface SponsorshipPartner {
  id: string;
  name: string;
  type: string;
  businessName: string;
  description: string;
  website: string;
  logoUrl: string;
  locations: SponsorLocation[];
  serviceAreas: string[];
  deliveryAvailable: boolean;
  onlineOrdering: boolean;
  partnershipLevel: string;
  contractStartDate: string;
  contractEndDate: string;
  monthlyBudget: number;
  costPerImpression: number;
  costPerClick: number;
  costPerConversion: number;
  sponsoredProducts: any[];
  coupons: any[];
  specialOffers: any[];
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  totalRevenue: number;
  communityRating: number;
  userReviews: any[];
  preferredByUsers: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SponsoredProduct {
  id: string;
  sponsorId: string;
  name: string;
  brand: string;
  description: string;
  category: string;
  imageUrl: string;
  nutritionHighlights: string[];
  averagePrice: number;
  priceRange: { min: number; max: number };
  availability: string;
  userRating: number;
  userReviewCount: number;
  usedInRecipes: number;
  targetDemographics: string[];
  targetDietaryPreferences: string[];
  targetCookingSkills: string[];
  isActive: boolean;
  createdAt: string;
}

export interface SponsorCoupon {
  id: string;
  sponsorId: string;
  title: string;
  description: string;
  discountType: string;
  discountValue: number;
  applicableProducts: string[];
  userEligibility: string[];
  usageLimit: number;
  usagePerUser: number;
  validFrom: string;
  validUntil: string;
  timesUsed: number;
  totalSavings: number;
  userSatisfaction: number;
  showInRecipes: boolean;
  showInGroceryLists: boolean;
  showInMealPlans: boolean;
  isActive: boolean;
  createdAt: string;
}

export const mockSponsors: SponsorshipPartner[] = [
  {
    id: 'whole-foods-1',
    name: 'Whole Foods Market',
    type: 'grocery_store',
    businessName: 'Whole Foods Market',
    description: 'Premium organic and natural foods',
    website: 'https://wholefoodsmarket.com',
    logoUrl: '/sponsors/whole-foods-logo.png',
    locations: [{
      id: 'wf-downtown',
      name: 'Downtown Location',
      address: '123 Main St',
      city: 'Your City',
      state: 'ST',
      zipCode: '12345',
      hours: {},
      specialServices: ['organic', 'local', 'delivery']
    }],
    serviceAreas: ['12345', '12346', '12347'],
    deliveryAvailable: true,
    onlineOrdering: true,
    partnershipLevel: 'gold',
    contractStartDate: '2024-01-01',
    contractEndDate: '2024-12-31',
    monthlyBudget: 5000,
    costPerImpression: 0.05,
    costPerClick: 0.25,
    costPerConversion: 2.50,
    sponsoredProducts: [],
    coupons: [],
    specialOffers: [],
    totalImpressions: 125000,
    totalClicks: 3200,
    totalConversions: 156,
    totalRevenue: 12500,
    communityRating: 4.6,
    userReviews: [],
    preferredByUsers: 847,
    isActive: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-06-01'
  },
  {
    id: 'farmers-market-1',
    name: 'Local Farmers Market',
    type: 'grocery_store',
    businessName: 'Downtown Farmers Market',
    description: 'Fresh local produce and artisanal goods',
    website: 'https://localfarmersmarket.com',
    logoUrl: '/sponsors/farmers-market-logo.png',
    locations: [{
      id: 'fm-downtown',
      name: 'Downtown Market',
      address: '456 Market St',
      city: 'Your City',
      state: 'ST',
      zipCode: '12345',
      hours: {},
      specialServices: ['local', 'organic', 'seasonal']
    }],
    serviceAreas: ['12345'],
    deliveryAvailable: false,
    onlineOrdering: false,
    partnershipLevel: 'silver',
    contractStartDate: '2024-01-01',
    contractEndDate: '2024-12-31',
    monthlyBudget: 2000,
    costPerImpression: 0.03,
    costPerClick: 0.15,
    costPerConversion: 1.50,
    sponsoredProducts: [],
    coupons: [],
    specialOffers: [],
    totalImpressions: 45000,
    totalClicks: 1200,
    totalConversions: 78,
    totalRevenue: 4500,
    communityRating: 4.8,
    userReviews: [],
    preferredByUsers: 234,
    isActive: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-06-01'
  }
];

export const mockSponsoredProducts: SponsoredProduct[] = [
  {
    id: 'organic-tomatoes',
    sponsorId: 'whole-foods-1',
    name: 'Organic Heirloom Tomatoes',
    brand: 'Whole Foods 365',
    description: 'Vine-ripened organic heirloom tomatoes',
    category: 'produce',
    imageUrl: '/products/tomatoes.jpg',
    nutritionHighlights: ['organic', 'high-vitamin-c'],
    averagePrice: 4.99,
    priceRange: { min: 3.99, max: 5.99 },
    availability: 'seasonal',
    userRating: 4.8,
    userReviewCount: 127,
    usedInRecipes: 89,
    targetDemographics: ['health_conscious'],
    targetDietaryPreferences: ['organic'],
    targetCookingSkills: ['beginner'],
    isActive: true,
    createdAt: '2024-03-01'
  },
  {
    id: 'local-honey',
    sponsorId: 'farmers-market-1',
    name: 'Raw Local Honey',
    brand: 'Beekeepers Collective',
    description: 'Unfiltered honey from local beekeepers',
    category: 'pantry',
    imageUrl: '/products/honey.jpg',
    nutritionHighlights: ['natural', 'unprocessed'],
    averagePrice: 12.99,
    priceRange: { min: 10.99, max: 14.99 },
    availability: 'year_round',
    userRating: 4.9,
    userReviewCount: 89,
    usedInRecipes: 156,
    targetDemographics: ['health_conscious', 'local_supporters'],
    targetDietaryPreferences: ['natural', 'local'],
    targetCookingSkills: ['beginner'],
    isActive: true,
    createdAt: '2024-03-01'
  }
];

export const mockSponsorCoupons: SponsorCoupon[] = [
  {
    id: 'summer-produce-20',
    sponsorId: 'whole-foods-1',
    title: '20% Off Summer Produce',
    description: 'Get 20% off fresh summer produce',
    discountType: 'percentage',
    discountValue: 20,
    applicableProducts: ['produce'],
    userEligibility: ['new_users'],
    usageLimit: 100,
    usagePerUser: 1,
    validFrom: '2024-06-01',
    validUntil: '2024-08-31',
    timesUsed: 23,
    totalSavings: 287.50,
    userSatisfaction: 4.8,
    showInRecipes: true,
    showInGroceryLists: true,
    showInMealPlans: true,
    isActive: true,
    createdAt: '2024-06-01'
  },
  {
    id: 'local-honey-15',
    sponsorId: 'farmers-market-1',
    title: '$3 Off Local Honey',
    description: 'Save $3 on any local honey purchase',
    discountType: 'fixed',
    discountValue: 3,
    applicableProducts: ['honey', 'pantry'],
    userEligibility: ['all_users'],
    usageLimit: 50,
    usagePerUser: 2,
    validFrom: '2024-06-01',
    validUntil: '2024-12-31',
    timesUsed: 12,
    totalSavings: 36.00,
    userSatisfaction: 4.9,
    showInRecipes: true,
    showInGroceryLists: true,
    showInMealPlans: true,
    isActive: true,
    createdAt: '2024-06-01'
  }
]; 